import { useEffect, useRef, useState } from "react";

export interface IUseVideoControllerProps {
  id: string;
}

const useVideoController = ({ id }: IUseVideoControllerProps) => {
  const [targetVideoElement, setTargetVideoElement] =
    useState<HTMLVideoElement | null>(null);

  /** 控制面板定时器 */
  const cancelControllerTimerRef = useRef<number | null>(null);

  /** 是否展示控制面板 */
  const [showController, setShowController] = useState(true);

  /** 控制播放暂停 */
  const [isPlaying, setIsPlaying] = useState(false);

  /** 当前播放秒数 */
  const [currentPlayTime, setCurrentPlayTime] = useState(0);

  /** 视频总时长 */
  const [totalDuration, setTotalDuration] = useState(0);

  /** 是否已经播放结束 */
  const [isEnded, setIsEnded] = useState(false);

  /** 是否在全屏状态 */
  const [isFullScreen, setIsFullScreen] = useState(false);

  const handlePlayOrPause = () => {
    if (isEnded) {
      return;
    }
    if (targetVideoElement) {
      if (isPlaying) {
        targetVideoElement.pause();
      } else {
        targetVideoElement.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleFullScreenChange = () => {
    if (isFullScreen) {
      document?.exitFullscreen();
    } else {
      targetVideoElement?.parentElement?.requestFullscreen();
    }
  };

  useEffect(() => {
    const handleMouseMoveInVideo = () => {
      setShowController(true);
      if (cancelControllerTimerRef.current) {
        clearTimeout(cancelControllerTimerRef.current);
      }
      cancelControllerTimerRef.current = window.setTimeout(() => {
        setShowController(false);
      }, 5000);
    };

    const videoElement = document.getElementById(id) as HTMLVideoElement;
    setTargetVideoElement(videoElement);
    const updateDuration = () => { setTotalDuration(Number.isFinite(videoElement.duration) ? videoElement.duration : 0); };
    const updateTime = () => { setCurrentPlayTime(videoElement.currentTime); };
    const onEnded = () => setIsEnded(true);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onFullscreen = () => setIsFullScreen(Boolean(document.fullscreenElement));
    updateDuration();
    videoElement.addEventListener('loadedmetadata', updateDuration);
    videoElement.addEventListener('timeupdate', updateTime);
    videoElement.addEventListener('ended', onEnded);
    videoElement.addEventListener('play', onPlay);
    videoElement.addEventListener('pause', onPause);
    videoElement.parentElement?.addEventListener('mousemove', handleMouseMoveInVideo);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => {
      videoElement.removeEventListener('loadedmetadata', updateDuration);
      videoElement.removeEventListener('timeupdate', updateTime);
      videoElement.removeEventListener('ended', onEnded);
      videoElement.removeEventListener('play', onPlay);
      videoElement.removeEventListener('pause', onPause);
      videoElement.parentElement?.removeEventListener('mousemove', handleMouseMoveInVideo);
      document.removeEventListener('fullscreenchange', onFullscreen);
      if (cancelControllerTimerRef.current) clearTimeout(cancelControllerTimerRef.current);
    };
  }, [id]);

  const getFormattedTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const leftSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${leftSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  return {
    isEnded,
    isPlaying,
    handlePlayOrPause,
    currentPlayTimeString: getFormattedTime(currentPlayTime),
    currentPlayTime,
    totalDuration: getFormattedTime(totalDuration),
    isFullScreen,
    handleFullScreenChange,
    showController,
  };
};

export default useVideoController;
